import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Card, CardHeader, CardContent, Button, Select, Badge, DataTable, Modal,
    type ColumnDef, type SortState,
} from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import {
    schoolsApi,
    type StudentReach, type TenantTeacher, type SchoolClass,
} from '../../../api/schoolsApi';
import { PUBLIC_TENANT_ID } from '../../../api/tenantsApi';

const REACH_LABELS: Record<StudentReach['kind'], string> = {
    enrolled: 'Their own students',
    classes: 'Named classes',
    tenant: 'Every student in this tenant',
};

/** Ordered so the widest reach sorts last — an administrator scanning the column
 *  is looking for who sees more than their own students. */
const REACH_ORDER: Record<StudentReach['kind'], number> = { enrolled: 0, classes: 1, tenant: 2 };

export function TeachingStaffCard({ tenantId }: { tenantId: string }) {
    // The public tenant is not a school. "Every other student in it" is every
    // unaffiliated user of the product, so the backend refuses to widen anyone
    // here — say so rather than showing a control that cannot work.
    const isPublic = tenantId === PUBLIC_TENANT_ID;
    const subjectLabel = useSubjectLabel();
    const [sort, setSort] = useState<SortState>({ sortBy: 'name', sortOrder: 'asc' });
    const [editing, setEditing] = useState<string | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ['tenant-teachers', tenantId],
        queryFn: () => schoolsApi.listTenantTeachers(tenantId),
        enabled: !isPublic,
    });

    /** Every class of every school this tenant owns — what `Named classes` picks from. */
    const { data: classes } = useQuery({
        queryKey: ['tenant-classes', tenantId],
        queryFn: async () => {
            const { schools } = await schoolsApi.listSchools({ tenantId });
            const lists = await Promise.all(schools.map(s => schoolsApi.listClasses(s.id)));
            return lists.flatMap(l => l.classes);
        },
        enabled: !isPublic,
    });

    const classLabel = useMemo(() => {
        const byId = new Map((classes ?? []).map(c => [c.id, `${c.academicYear} · Grade ${c.grade}`]));
        return (id: string) => byId.get(id) ?? id;
    }, [classes]);

    const columns: ColumnDef<TenantTeacher>[] = useMemo(() => [
        {
            key: 'name', header: 'Teacher', sortable: true,
            sortValue: t => t.name.toLowerCase(),
            render: t => (
                <>
                    {t.name}
                    {t.loginId && <div className="tenant-detail-note">{t.loginId}</div>}
                </>
            ),
        },
        {
            key: 'subjects', header: 'Subjects', sortable: true,
            sortValue: t => t.subjects.length,
            // Display names, never the internal keys — "english_literature" is a
            // storage key and reads as a bug on a page a school administrator uses.
            render: t => (t.subjects.length ? t.subjects.map(subjectLabel).join(', ') : '—'),
        },
        {
            key: 'reach', header: 'Sees', sortable: true,
            sortValue: t => REACH_ORDER[t.reach],
            render: t => (
                <>
                    <Badge variant={t.reach === 'enrolled' ? 'default' : 'info'}>
                        {REACH_LABELS[t.reach]}
                    </Badge>
                    {t.reach === 'classes' && (
                        <div className="tenant-detail-note">
                            {(t.classIds ?? []).map(classLabel).join(', ')}
                        </div>
                    )}
                </>
            ),
        },
        {
            key: 'actions', header: '',
            render: t => (
                <Button variant="ghost" onClick={() => setEditing(t.id)}>Change</Button>
            ),
        },
    ], [subjectLabel, classLabel]);

    const teacher = data?.teachers.find(t => t.id === editing);

    return (
        <Card padding="none">
            <CardHeader
                title="Teaching staff"
                subtitle="Who may see which students. Teaching relationships are assigned on each class."
            />
            <CardContent>
                {isPublic && (
                    <p className="tenant-detail-note">
                        This is the public tenant, not a school. Reach cannot be widened here —
                        every other student in it is an unaffiliated user of the product, not a
                        classmate.
                    </p>
                )}
                {!isPublic && error && (
                    <p className="tenant-detail-note">{(error as Error).message}</p>
                )}
                {!isPublic && !error && (
                    <DataTable
                        columns={columns}
                        data={data?.teachers ?? []}
                        rowKey={t => t.id}
                        loading={isLoading}
                        emptyMessage="No teachers yet. They are created from this school's staff list."
                        bordered
                        sort={sort}
                        onSortChange={setSort}
                    />
                )}
                {teacher && (
                    <ReachEditor
                        tenantId={tenantId}
                        teacher={teacher}
                        classes={classes ?? []}
                        classLabel={classLabel}
                        onClose={() => setEditing(null)}
                    />
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Changing one teacher's reach.
 *
 * A dialog rather than an inline row edit: `Named classes` is a list of
 * checkboxes that would push every row below it down the page, and the choice is
 * deliberate enough to deserve its own Save.
 */
function ReachEditor({
    tenantId, teacher, classes, classLabel, onClose,
}: {
    tenantId: string;
    teacher: TenantTeacher;
    classes: SchoolClass[];
    classLabel: (id: string) => string;
    onClose: () => void;
}) {
    const queryClient = useQueryClient();
    const [kind, setKind] = useState<StudentReach['kind']>(teacher.reach);
    const [classIds, setClassIds] = useState<string[]>(teacher.classIds ?? []);

    const save = useMutation({
        mutationFn: () => schoolsApi.setTeacherReach(
            tenantId, teacher.id,
            kind === 'classes' ? { kind: 'classes', classIds } : { kind },
        ),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['tenant-teachers', tenantId] });
            onClose();
        },
    });

    // `classes` with nothing named would store a setting that grants nothing; the
    // backend rejects an empty list, so the button says why before it is pressed.
    const incomplete = kind === 'classes' && classIds.length === 0;

    return (
        <Modal isOpen onClose={onClose} title={`What ${teacher.name} may see`}>
            <div className="tenant-staff-edit">
                <Select
                    aria-label={`What ${teacher.name} may see`}
                    value={kind}
                    onChange={e => setKind(e.target.value as StudentReach['kind'])}
                >
                    {(Object.keys(REACH_LABELS) as StudentReach['kind'][]).map(k => (
                        <option key={k} value={k}>{REACH_LABELS[k]}</option>
                    ))}
                </Select>

                <p className="tenant-detail-note">
                    Reach lets somebody look. It does not make them a teacher of these
                    students — handing work in for them, or changing their marks, stays with
                    whoever actually teaches them.
                </p>

                {kind === 'classes' && (
                    <div className="tenant-staff-classes">
                        {classes.length === 0 && (
                            <span className="tenant-detail-note">This tenant has no classes yet.</span>
                        )}
                        {classes.map(c => (
                            <label key={c.id}>
                                <input
                                    type="checkbox"
                                    checked={classIds.includes(c.id)}
                                    onChange={e => setClassIds(ids => (
                                        e.target.checked ? [...ids, c.id] : ids.filter(id => id !== c.id)
                                    ))}
                                />
                                {classLabel(c.id)}
                            </label>
                        ))}
                    </div>
                )}

                <div className="tenant-staff-actions">
                    <Button variant="primary" disabled={save.isPending || incomplete} onClick={() => save.mutate()}>
                        {save.isPending ? 'Saving…' : 'Save'}
                    </Button>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    {incomplete && <span className="tenant-detail-note">Pick at least one class.</span>}
                    {save.error && <span className="tenant-detail-note">{(save.error as Error).message}</span>}
                </div>
            </div>
        </Modal>
    );
}
